

import { type Context, type ReactNode, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import * as ToastPrimitive from '@radix-ui/react-toast';

import { toast, toastViewport } from './Toast.css';
import { NotificationBox, NotificationProps } from '../Notification/NotificationBox';

const DEFAULT_DURATION = 3000;

type ToastPosition = 'top' | 'bottom' | 'left' | 'right';

export type ToastConfig = {
  open: boolean;
  position: ToastPosition;
  duration: number;
  notification: NotificationProps;
};

type ToastContextType = {
  config: ToastConfig;
  showToast: (
    props: NotificationProps & {
      position?: ToastConfig['position'];
      duration?: ToastConfig['duration'];
    },
  ) => void;
  hideToast: () => void;
};

const initialConfig: ToastConfig = {
  open: false,
  position: 'bottom',
  duration: DEFAULT_DURATION,
  notification: {} as NotificationProps,
};

// Use a singleton context to prevent duplication when module is evaluated multiple times
// (e.g. due to vi.mock creating separate module evaluation graphs in Vitest)
const TOAST_CONTEXT_KEY = '__eneco_ToastContext__';
export const ToastContext: Context<ToastContextType | null> =
  (globalThis as Record<string, unknown>)[TOAST_CONTEXT_KEY] as Context<ToastContextType | null> ||
  ((globalThis as Record<string, unknown>)[TOAST_CONTEXT_KEY] = createContext<ToastContextType | null>(null));

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<ToastConfig>(initialConfig);

  const showToast = useCallback(
    (
      props: NotificationProps & {
        position?: 'top' | 'bottom' | 'left' | 'right';
        duration?: number;
      },
    ) => {
      const { position = initialConfig.position, duration = initialConfig.duration, ...notification } = props;
      setConfig({
        open: true,
        position,
        duration,
        notification,
      });
    },
    [],
  );

  const hideToast = useCallback(() => setConfig(prev => ({ ...prev, open: false })), []);

  const value = useMemo(() => ({ config, showToast, hideToast }), [config, showToast, hideToast]);

  return (
    <ToastContext.Provider value={value}>
      <Toast />
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (defaults?: { position?: 'top' | 'bottom' | 'left' | 'right'; duration?: number }) => {
  const frozenDefaults = useRef<{ position?: 'top' | 'bottom' | 'left' | 'right'; duration?: number }>(defaults);

  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within SparkyProvider');

  const contextShowToast = context.showToast;
  const contextHideToast = context.hideToast;

  const showToast = useCallback(
    (
      props: NotificationProps & {
        position?: ToastConfig['position'];
        duration?: ToastConfig['duration'];
      },
    ) => {
      contextShowToast({
        position: frozenDefaults.current?.position ?? 'bottom',
        duration: frozenDefaults.current?.duration ?? DEFAULT_DURATION,
        ...props,
      });
    },
    [contextShowToast],
  );

  return useMemo(
    () => ({
      showToast: showToast,
      hideToast: contextHideToast,
    }),
    [contextHideToast, showToast],
  );
};

export const Toast = () => {
  const context = useContext(ToastContext);
  if (!context) return null;

  const { config, hideToast } = context;

  if (!config.notification?.text || !config.notification?.title) {
    return null;
  }

  return (
    <ToastPrimitive.Provider swipeDirection={getSwipeDirection(config.position)}>
      <ToastPrimitive.Root
        duration={config.duration}
        open={config.open}
        onOpenChange={hideToast}
        className={toast({ position: config.position })}>
        <NotificationBox {...config.notification} />
      </ToastPrimitive.Root>
      <ToastPrimitive.Viewport className={toastViewport({ position: config.position })} />
    </ToastPrimitive.Provider>
  );
};

const getSwipeDirection = (position: ToastConfig['position']) => {
  switch (position) {
    case 'bottom':
    case 'top':
      return 'down';
    default:
      return position;
  }
};
